import { useMemo, useState } from "react";
import useAccountPageParams from "../../../../hooks/useAccountPageParams";
import { MagnifyingGlassIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import {
    Alert,
    Divider,
    Group,
    LoadingOverlay,
    Pagination,
    ScrollArea,
    Select,
    Stack,
    Text,
    TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import type { AssociatedObject } from "../../../../shared/emails-meetings/types";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import EmailListItem from "../../../../shared/emails-meetings/EmailListItem";
import EmailCard from "../../../../shared/emails-meetings/EmailCard";
import EmailDetailsDrawer from "../../../../shared/emails-meetings/EmailDetailsDrawer";
import { emailMatchesQuery } from "../../../../shared/emails-meetings/emailUtils";
import EmptyTabState from "../../../../shared/ui/EmptyTabState";
import ViewModeToggle from "../../../../shared/ui/ViewModeToggle";
import { parseObjectSearchRecord } from "../../../../shared/emails-meetings/objectSearchAdapter";

const AccountEmails = () => {
    const { accountId }: any = useAccountPageParams();
    const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<AssociatedObject | null>(null);
    const [viewMode, setViewMode] = useState<"list" | "card">("list");

    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    // Scope filter: only conversations associated with this account
    const scopedFilter = useMemo(() => ({
        query_type: "AND",
        predicates: [{
            field: "accounts.external_id",
            operator: "eq",
            type: "string",
            value: accountId,
            entity_type: "column",
        }],
    }), [accountId]);

    const {
        rows,
        total,
        pageCount,
        pageIndex,
        setPageIndex,
        isLoading,
        isFetching,
        error,
    } = useObjectSearchTable<AssociatedObject>({
        workspaceId: workspaceId || "",
        entityType: "conversations",
        enabled: !!workspaceId && !!accountId,
        initialPageSize: 50,
        initialSort: {
            field: "conversations.created_at",
            order: sortOrder,
        },
        queryOptions: {
            filter: scopedFilter,
            fallbackSortField: "conversations.created_at",
        },
        parseRows: (rawRows: any[]) =>
            (rawRows || []).map((record: any) => parseObjectSearchRecord(record, "conversations")),
        resetPageDeps: [accountId],
    });

    // Client-side search filtering on the current page
    const filteredEmails = useMemo(() => {
        if (!debouncedSearchQuery) return rows;
        return rows.filter((email: AssociatedObject) => emailMatchesQuery(email, debouncedSearchQuery));
    }, [rows, debouncedSearchQuery]);

    const displayTotal = debouncedSearchQuery ? filteredEmails.length : total;
    const totalPages = pageCount;

    const handleOpenEmail = (email: AssociatedObject) => {
        setSelectedEmail(email);
        setDetailsOpen(true);
    };

    const handleSortChange = (value: string | null) => {
        if (value === "newest") {
            setSortOrder("desc");
        } else if (value === "oldest") {
            setSortOrder("asc");
        }
    };

    const handlePageChange = (page: number) => {
        setPageIndex(page - 1); // Mantine Pagination is 1-indexed, hook is 0-indexed
    };

    return (
        <div className="h-full bg-white flex flex-col overflow-hidden">
            <LoadingOverlay
                loaderProps={{ size: "sm" }}
                c="main.5"
                visible={isLoading}
                overlayProps={{ blur: 2 }}
            />

            <Stack gap={10} className="px-4 pt-4 pb-3">
                <Group justify="space-between" align="flex-start">
                    <Stack gap={2}>
                        <Text fw={700} c="text.9" size="lg">Emails</Text>
                        <Text size="xs" c="text.5">
                            {isLoading ? "..." : displayTotal} emails
                        </Text>
                    </Stack>
                    <Group gap="sm">
                        <ViewModeToggle value={viewMode} onChange={setViewMode} />
                        <Select
                            size="xs"
                            placeholder="Sort by"
                            data={[
                                { value: "newest", label: "Date (Newest)" },
                                { value: "oldest", label: "Date (Oldest)" },
                            ]}
                            value={sortOrder === "asc" ? "oldest" : "newest"}
                            onChange={handleSortChange}
                            allowDeselect={false}
                            w={140}
                        />
                    </Group>
                </Group>

                <TextInput
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.currentTarget.value);
                    }}
                    placeholder="Search subject, sender, recipients..."
                    leftSection={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
                    size="sm"
                    radius="md"
                />
            </Stack>

            <Divider />

            {/* Content */}
            <ScrollArea className="flex-1" type="scroll" scrollHideDelay={500}>
                <Stack gap={0}>
                    {!!error && (
                        <div className="p-4">
                            <Alert color="red" variant="light" radius="md">
                                Failed to load emails. Please try again.
                            </Alert>
                        </div>
                    )}

                    {!isLoading && !error && filteredEmails.length === 0 && !isFetching && (
                        <EmptyTabState
                            title="No emails yet"
                            description={searchQuery ? "Try adjusting your search query." : "Emails associated with this account will appear here."}
                            icon={<EnvelopeIcon className="h-6 w-6" />}
                        />
                    )}

                    {viewMode === "card" ? (
                        <div className="grid gap-3 p-4">
                            {filteredEmails.map((email: any) => (
                                <EmailCard key={email.id} email={email} />
                            ))}
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {filteredEmails.map((email: any) => (
                                <EmailListItem key={email.id} email={email} onOpen={handleOpenEmail} />
                            ))}
                        </div>
                    )}
                </Stack>
            </ScrollArea>

            {/* Pagination Footer */}
            {totalPages > 1 && (
                <div className="p-3 border-t border-gray-100 bg-gray-50">
                    <Group justify="center">
                        <Pagination
                            total={totalPages}
                            value={pageIndex + 1}
                            onChange={handlePageChange}
                            size="sm"
                            color="main"
                            siblings={1}
                        />
                    </Group>
                </div>
            )}

            <EmailDetailsDrawer
                opened={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                email={selectedEmail}
            />
        </div>
    );
};

export default AccountEmails;
